import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Sidebar } from 'primereact/sidebar';
import { AppCard, AppPage, CopyableText } from '../../components/general';
import { toLocalDateTime } from '../../utils/date-helper';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWaterfallExecutionDetail } from '../../hooks/useVoxTracing';
import { getFileContent } from '../../services/voxTracingService';
import {
  WaterfallStepTraceViewModel,
  VoxOrderHistorySummaryViewModel,
  WebhookEventViewModel,
} from '../../models/voxTracing';

const WaterfallExecutionDetail = () => {
  const { waterfallGuid } = useParams<{ waterfallGuid: string }>();
  const navigate = useNavigate();

  const toast = useRef<Toast>(null);
  const queryClient = useQueryClient();
  const { detailQuery } = useWaterfallExecutionDetail(waterfallGuid);

  const [selectedWebhook, setSelectedWebhook] = useState<WebhookEventViewModel | null>(null);
  const [webhookPanelVisible, setWebhookPanelVisible] = useState(false);

  const webhookPayloadQuery = useQuery<string>({
    queryKey: ['file-content', selectedWebhook?.requestPayloadFileId],
    queryFn: () => getFileContent(selectedWebhook!.requestPayloadFileId!),
    enabled: !!selectedWebhook?.requestPayloadFileId && webhookPanelVisible,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const handleStepClick = (e: any) => {
    const step = e.value as WaterfallStepTraceViewModel;
    if (step?.voxOrderHistory?.voxOrderHistoryGuid) {
      navigate(`/vox-tracing/order/${step.voxOrderHistory.voxOrderHistoryGuid}`);
    }
  };

  const formatDateTime = (value: string | null) => {
    return value ? toLocalDateTime(value) : '—';
  };

  const stepResultTemplate = (rowData: WaterfallStepTraceViewModel) => {
    const severityMap: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      Success: 'success',
      Pending: 'info',
      AwaitingCallback: 'info',
      Canceled: 'warning',
      Failed: 'danger',
      Timeout: 'warning',
    };
    return <Tag value={rowData.stepResult} severity={severityMap[rowData.stepResult] || 'info'} />;
  };

  const vendorTemplate = (rowData: WaterfallStepTraceViewModel) => {
    return rowData.voxOrderHistory?.integrationVendor || '—';
  };

  const statusTemplate = (rowData: WaterfallStepTraceViewModel) => {
    if (!rowData.voxOrderHistory) return '—';
    const severityMap: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      Success: 'success',
      Pending: 'info',
      AwaitingCallback: 'info',
      Failed: 'danger',
      Error: 'danger',
      Timeout: 'warning',
    };
    return (
      <Tag
        value={rowData.voxOrderHistory.status}
        severity={severityMap[rowData.voxOrderHistory.status] || 'info'}
      />
    );
  };

  const dateStartedTemplate = (rowData: WaterfallStepTraceViewModel) => {
    return formatDateTime(rowData.dateStarted);
  };

  const dateCompletedTemplate = (rowData: WaterfallStepTraceViewModel) => {
    return formatDateTime(rowData.dateCompleted);
  };

  const dataStatusTemplate = (rowData: WaterfallStepTraceViewModel) => {
    const voie = rowData.voieProviderDataStatus;
    const voa = rowData.voaProviderDataStatus;

    const hasVoieData = voie === 'IncomeAndEmployment' || voie === 'EmploymentOnly';
    const hasVoaData = voa === 'Assets' || voa === 'AssetsAndIncome';

    if (hasVoieData || hasVoaData) {
      const chips: { label: string; severity: 'success' | 'info' | 'warning' | 'danger' }[] = [];
      if (voie === 'IncomeAndEmployment') chips.push({ label: 'VOIE', severity: 'success' });
      if (voie === 'EmploymentOnly') chips.push({ label: 'VOE', severity: 'info' });
      if (hasVoaData) chips.push({ label: 'VOA', severity: 'success' });
      return (
        <div className="flex gap-1 flex-wrap">
          {chips.map((chip, idx) => (
            <Tag key={idx} value={chip.label} severity={chip.severity} />
          ))}
        </div>
      );
    }

    if (voie === 'NoData' || voa === 'NoData') {
      return <Tag value="NoData" severity="danger" />;
    }

    return <Tag value="Unknown" severity="warning" />;
  };

  const executionStatusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    Complete: 'success',
    Running: 'info',
    Error: 'danger',
    Abort: 'warning',
  };

  const relatedOrderStatusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    Success: 'success',
    Pending: 'info',
    AwaitingCallback: 'info',
    Failed: 'danger',
    Error: 'danger',
    Timeout: 'warning',
  };

  const handleRelatedOrderClick = (e: { value: VoxOrderHistorySummaryViewModel }) => {
    const order = e.value;
    if (order?.voxOrderHistoryGuid) {
      navigate(`/vox-tracing/order/${order.voxOrderHistoryGuid}`);
    }
  };

  const relatedOrderStatusTemplate = (rowData: VoxOrderHistorySummaryViewModel) => {
    return <Tag value={rowData.status} severity={relatedOrderStatusSeverity[rowData.status] || 'info'} />;
  };

  const relatedOrderDateTemplate = (rowData: VoxOrderHistorySummaryViewModel) => {
    return formatDateTime(rowData.createdDate);
  };

  const formatJson = (str: string | null): string => {
    if (!str) return '';
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  if (detailQuery.isLoading) {
    return (
      <AppPage title="Waterfall Execution Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Waterfall Detail' }]}>
        <div className="flex justify-content-center p-5">
          <ProgressSpinner />
        </div>
      </AppPage>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <AppPage title="Waterfall Execution Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Waterfall Detail' }]}>
        <AppCard>
          <div className="text-red-500">
            {detailQuery.isError
              ? `Error: ${detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load'}`
              : 'Waterfall execution record not found'}
          </div>
          <Button label="Back to Search" icon="pi pi-arrow-left" className="mt-3" onClick={() => navigate('/vox-tracing')} />
        </AppCard>
      </AppPage>
    );
  }

  const detail = detailQuery.data;

  const allRelatedOrders = detail.relatedOrders ?? [];

  return (
    <AppPage title="Waterfall Execution Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Waterfall Detail' }]}>
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-center mb-3">
        <Button
          label="Back to Search"
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={() => navigate('/vox-tracing')}
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          className="p-button-outlined p-button-sm"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['waterfall-execution-detail'] })}
          loading={detailQuery.isRefetching}
        />
      </div>

      <AppCard>
        <div className="flex flex-column gap-4">
          <div className="flex flex-row justify-content-between align-items-center">
            <h2 className="text-xl font-bold m-0">
              {detail.waterfallName || `Waterfall #${detail.waterfallId}`}
            </h2>
            <Tag
              value={detail.status}
              severity={executionStatusSeverity[detail.status] || 'info'}
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <span className="text-500 text-sm">Loan Number</span>
              <CopyableText value={detail.loanNumber} label="Loan Number" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Loan Identifier</span>
              <CopyableText value={detail.loanIdentifier} label="Loan Identifier" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Lead Number</span>
              <CopyableText value={detail.leadNumber} label="Lead Number" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Date Started</span>
              <div className="font-semibold">{formatDateTime(detail.dateStarted)}</div>
            </div>
            <div>
              <span className="text-500 text-sm">Date Completed</span>
              <div className="font-semibold">{formatDateTime(detail.dateCompleted)}</div>
            </div>
            <div>
              <span className="text-500 text-sm">Execution GUID</span>
              <CopyableText value={detail.waterfallExecutionRecordGuid} label="Execution GUID" toast={toast} />
            </div>
          </div>
        </div>
      </AppCard>

      <AppCard className="mt-3">
        <h3 className="text-lg font-semibold mb-3">Waterfall Steps</h3>
        <PrimeDataTable
          value={detail.steps}
          selectionMode="single"
          onSelectionChange={handleStepClick}
          emptyMessage="No step trace data"
          className="cursor-pointer"
          rowClassName={(data: WaterfallStepTraceViewModel) =>
            data.voxOrderHistory ? '' : 'opacity-60'
          }
        >
          <Column field="stepResult" header="STEP RESULT" body={stepResultTemplate} />
          <Column header="VENDOR" body={vendorTemplate} />
          <Column header="ORDER STATUS" body={statusTemplate} />
          <Column field="reportType" header="REPORT TYPE" />
          <Column header="DATA STATUS" body={dataStatusTemplate} />
          <Column header="DATE STARTED" body={dateStartedTemplate} />
          <Column header="DATE COMPLETED" body={dateCompletedTemplate} />
          <Column field="statusMessage" header="MESSAGE" />
        </PrimeDataTable>
      </AppCard>

      {detail.webhookEvents && detail.webhookEvents.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Webhook Events</h3>
          <PrimeDataTable
            value={detail.webhookEvents}
            selectionMode="single"
            onSelectionChange={(e: any) => {
              setSelectedWebhook(e.value as WebhookEventViewModel);
              setWebhookPanelVisible(true);
            }}
            emptyMessage="No webhook events"
            className="cursor-pointer"
            paginator
            rows={10}
          >
            <Column
              field="eventTimestamp"
              header="TIMESTAMP"
              body={(row: WebhookEventViewModel) => formatDateTime(row.eventTimestamp)}
              sortable
            />
            <Column field="eventType" header="EVENT TYPE" sortable />
            <Column
              field="httpStatus"
              header="HTTP STATUS"
              body={(row: WebhookEventViewModel) => (
                <Tag
                  value={String(row.httpStatus)}
                  severity={row.httpStatus >= 200 && row.httpStatus < 300 ? 'success' : 'danger'}
                />
              )}
              sortable
            />
            <Column field="vendor" header="VENDOR" sortable />
            <Column field="attempt" header="ATTEMPT" />
          </PrimeDataTable>
        </AppCard>
      )}

      {allRelatedOrders.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Related Orders</h3>
          <PrimeDataTable
            value={allRelatedOrders}
            selectionMode="single"
            onSelectionChange={handleRelatedOrderClick}
            emptyMessage="No related orders"
            className="cursor-pointer"
          >
            <Column field="transactionName" header="TRANSACTION" sortable />
            <Column field="integrationVendor" header="VENDOR" sortable />
            <Column field="status" header="ORDER STATUS" body={relatedOrderStatusTemplate} sortable />
            <Column header="DATA STATUS" body={(rowData: VoxOrderHistorySummaryViewModel) => {
              const voie = rowData.voieProviderDataStatus;
              const voa = rowData.voaProviderDataStatus;
              const hasVoieData = voie === 'IncomeAndEmployment' || voie === 'EmploymentOnly';
              const hasVoaData = voa === 'Assets' || voa === 'AssetsAndIncome';
              if (hasVoieData || hasVoaData) {
                const chips: { label: string; severity: 'success' | 'info' | 'warning' | 'danger' }[] = [];
                if (voie === 'IncomeAndEmployment') chips.push({ label: 'VOIE', severity: 'success' });
                if (voie === 'EmploymentOnly') chips.push({ label: 'VOE', severity: 'info' });
                if (hasVoaData) chips.push({ label: 'VOA', severity: 'success' });
                return (
                  <div className="flex gap-1 flex-wrap">
                    {chips.map((chip, idx) => (
                      <Tag key={idx} value={chip.label} severity={chip.severity} />
                    ))}
                  </div>
                );
              }
              if (voie === 'NoData' || voa === 'NoData') {
                return <Tag value="NoData" severity="danger" />;
              }
              return <Tag value="Unknown" severity="warning" />;
            }} />
            <Column header="DATE STARTED" body={(rowData: VoxOrderHistorySummaryViewModel) => formatDateTime(rowData.createdDate)} sortable />
            <Column header="DATE COMPLETED" body={(rowData: VoxOrderHistorySummaryViewModel) => formatDateTime(rowData.modifiedDate)} sortable />
          </PrimeDataTable>
        </AppCard>
      )}

      <Sidebar
        visible={webhookPanelVisible}
        position="right"
        onHide={() => setWebhookPanelVisible(false)}
        style={{ width: '50vw' }}
        header={
          <div className="flex flex-column">
            <span className="font-bold text-lg">Webhook Event Detail</span>
            {selectedWebhook && (
              <span className="text-500 text-sm">{formatDateTime(selectedWebhook.eventTimestamp)}</span>
            )}
          </div>
        }
      >
        {selectedWebhook && (
          <div className="flex flex-column gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-500 text-sm font-semibold">Event Type</span>
                <div>{selectedWebhook.eventType}</div>
              </div>
              <div>
                <span className="text-500 text-sm font-semibold">HTTP Status</span>
                <div>
                  <Tag
                    value={String(selectedWebhook.httpStatus)}
                    severity={selectedWebhook.httpStatus >= 200 && selectedWebhook.httpStatus < 300 ? 'success' : 'danger'}
                  />
                </div>
              </div>
              <div>
                <span className="text-500 text-sm font-semibold">Vendor</span>
                <div>{selectedWebhook.vendor || '\u2014'}</div>
              </div>
              <div>
                <span className="text-500 text-sm font-semibold">Attempt</span>
                <div>{selectedWebhook.attempt}</div>
              </div>
            </div>

            <div>
              <span className="text-500 text-sm font-semibold">URL</span>
              <div className="text-sm" style={{ wordBreak: 'break-all' }}>{selectedWebhook.url}</div>
            </div>

            {selectedWebhook.requestPayloadFileId && (
              <div>
                <span className="text-500 text-sm font-semibold">Request Payload</span>
                {webhookPayloadQuery.isLoading && (
                  <div className="flex align-items-center gap-2 py-3">
                    <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                    <span className="text-sm text-500">Loading payload...</span>
                  </div>
                )}
                {webhookPayloadQuery.isError && (
                  <div className="text-red-500 text-sm p-3">Failed to load request payload</div>
                )}
                {webhookPayloadQuery.data && (
                  <pre
                    className="surface-ground border-round p-3 text-sm overflow-auto m-0"
                    style={{ maxHeight: '400px' }}
                  >
                    {formatJson(webhookPayloadQuery.data)}
                  </pre>
                )}
              </div>
            )}

            {selectedWebhook.response && (
              <div>
                <span className="text-500 text-sm font-semibold">Response</span>
                <pre
                  className="surface-ground border-round p-3 text-sm overflow-auto m-0"
                  style={{ maxHeight: '300px' }}
                >
                  {formatJson(selectedWebhook.response)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Sidebar>
    </AppPage>
  );
};

export default WaterfallExecutionDetail;
